import {
  cn,
  UzBadge,
  UzButton,
  UzDropdownMenu,
  UzSelect,
  UzTabs,
  type UzDropdownMenuItem,
} from '@utazzo/ui';
import {
  ArrowRight,
  CalendarDays,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  FileCheck2,
  Globe2,
  Inbox,
  MapPin,
  RefreshCw,
  RotateCcw,
  Shuffle,
  UserRound,
  Users,
} from 'lucide-react';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ActionableCell } from '../components/ActionableCell';
import { DepartmentChip } from '../components/DepartmentChip';
import { DEPARTMENT_META } from '../item-kind';
import {
  useAssignOpsCase,
  useCompleteOpsCase,
  useOpenOpsCase,
  useOpsAssignees,
  useOpsCaseSummary,
  useOpsCasesList,
  useReopenOpsCase,
  useSetOpsCoordinator,
  useMarkAlreadyBooked,
} from '../use-ops-cases';

import type { OpsCaseListQuery } from '../ops-cases.api';
import type { DataGridColumn } from '@/components/data-grid';
import type {
  OpsAssignee,
  OpsCaseRow,
  OpsCaseView,
  OpsDesk,
  TravellerDocumentRequestStatus,
} from '@utazzo/shared';

import { ListScreen } from '@/components/list-screen';
import { PageShell } from '@/components/page-shell';
import { useCurrentUser } from '@/features/auth/use-auth';

/**
 * Converted leads - the operations team's inbox.
 *
 * A lead lands here once the contract is signed and the customer has uploaded
 * the travel documents; the round-robin deals it to an ops executive at work.
 * Laid out like the Lead inbox: KPI strip on top, tabs, one grid per tab.
 * Every row lists what is left to book (flights, hotels, activities,
 * transfers) straight from the converted itinerary.
 *
 * ADMIN / OPERATIONS_LEAD see the whole desk (plus the Unassigned queue, an
 * assignee filter and reassign); everyone else sees only their own leads -
 * the API enforces the same split.
 */

const OPEN_STATUSES = new Set<OpsCaseRow['status']>(['QUEUED', 'ASSIGNED', 'IN_PROGRESS']);

const VIEWS: OpsCaseView[] = ['fresh', 'unassigned', 'in_progress', 'completed', 'all'];
const isView = (v: string | null): v is OpsCaseView => VIEWS.includes(v as OpsCaseView);

const DOC_META: Record<
  TravellerDocumentRequestStatus,
  { label: string; tone: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' }
> = {
  PENDING: { label: 'Docs pending', tone: 'secondary' },
  PARTIAL: { label: 'Docs partial', tone: 'warning' },
  SUBMITTED: { label: 'Docs submitted', tone: 'default' },
  VERIFIED: { label: 'Docs verified', tone: 'success' },
  CANCELLED: { label: 'Docs cancelled', tone: 'secondary' },
};

const STATUS_META: Record<
  OpsCaseRow['status'],
  { label: string; tone: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' }
> = {
  QUEUED: { label: 'Unassigned', tone: 'warning' },
  ASSIGNED: { label: 'Fresh', tone: 'default' },
  IN_PROGRESS: { label: 'In progress', tone: 'secondary' },
  COMPLETED: { label: 'Completed', tone: 'success' },
  CANCELLED: { label: 'Cancelled', tone: 'destructive' },
};

const fmtDate = (d: Date | null): string =>
  d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const ago = (d: Date | null): string => {
  if (!d) return '-';
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60_000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

const daysToTravel = (d: Date | null): string | null => {
  if (!d) return null;
  const days = Math.ceil((d.getTime() - Date.now()) / 86_400_000);
  if (days < 0) return 'travelled';
  if (days === 0) return 'today';
  return `in ${days}d`;
};

const inr = (n: number | null): string =>
  n === null
    ? '-'
    : new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(n);

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

/** One desk's owner: a dropdown for managers, plain text for everyone else. */
const DeskAssignee = ({
  desk,
  isManager,
  assignees,
}: {
  desk: OpsDesk;
  isManager: boolean;
  assignees: OpsAssignee[];
}): React.JSX.Element => {
  const assign = useAssignOpsCase();
  const closed = desk.status === 'COMPLETED' || desk.status === 'CANCELLED';
  const label = (
    <span className="flex min-w-0 items-center gap-1.5">
      <UserRound className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
      <span className={cn('truncate', desk.assignedTo ? 'text-zinc-900' : 'text-amber-700')}>
        {desk.assignedTo?.fullName ?? 'Unassigned'}
      </span>
      {desk.assignmentSource === 'ROUND_ROBIN' ? (
        <span title="Dealt by round-robin">
          <Shuffle className="h-3 w-3 shrink-0 text-zinc-400" />
        </span>
      ) : null}
    </span>
  );
  if (!isManager || closed) return <div className="text-sm">{label}</div>;

  // The desk's own people first; a manager may still hand it to anyone in ops.
  const ordered = [...assignees].sort(
    (a, b) =>
      Number(b.opsDepartment === desk.department) - Number(a.opsDepartment === desk.department),
  );
  const items: UzDropdownMenuItem[] = [
    { type: 'label', label: `Assign to (${DEPARTMENT_META[desk.department].label} desk first)` },
    ...ordered.map<UzDropdownMenuItem>((a) => ({
      label: (
        <span className="flex w-full items-center gap-2">
          <span
            className={cn(
              'h-2 w-2 shrink-0 rounded-full',
              a.online ? 'bg-emerald-500' : 'bg-zinc-300',
            )}
          />
          <span className="flex-1 truncate">{a.fullName}</span>
          <span
            className={cn(
              'text-[11px]',
              a.opsDepartment === desk.department ? 'text-zinc-500' : 'text-zinc-400 italic',
            )}
          >
            {a.opsDepartment ? DEPARTMENT_META[a.opsDepartment].label : 'no desk'}
          </span>
          <span className="text-[11px] text-zinc-400 tabular-nums">{a.openCases} open</span>
        </span>
      ),
      disabled: a.id === desk.assignedTo?.id,
      onSelect: () => assign.mutate({ id: desk.id, userId: a.id }),
    })),
    ...(desk.assignedTo
      ? ([
          { type: 'separator' },
          {
            label: 'Back to unassigned queue',
            icon: <Inbox className="h-3.5 w-3.5" />,
            onSelect: () => assign.mutate({ id: desk.id, userId: null }),
          },
        ] as UzDropdownMenuItem[])
      : []),
  ];

  return (
    <div onClick={(e) => e.stopPropagation()} className="text-sm">
      <UzDropdownMenu
        align="start"
        contentClassName="max-h-72 overflow-y-auto"
        trigger={
          <button
            type="button"
            className="flex max-w-[200px] items-center gap-1 rounded-md px-1.5 py-1 hover:bg-zinc-100"
          >
            {label}
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
          </button>
        }
        items={items}
      />
    </div>
  );
};

/**
 * The lead's Operations owner (OPS role): sees every desk, cross-checks the
 * executives' holds and asks for fund. Managers can hand the lead over.
 */
const CoordinatorLine = ({
  row,
  isManager,
  assignees,
}: {
  row: OpsCaseRow;
  isManager: boolean;
  assignees: OpsAssignee[];
}): React.JSX.Element => {
  const setOwner = useSetOpsCoordinator();
  const label = (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="text-[10px] font-semibold text-zinc-500 uppercase">Operations</span>
      <span className={cn('truncate', row.coordinator ? 'text-zinc-900' : 'text-amber-700')}>
        {row.coordinator?.fullName ?? 'No owner yet'}
      </span>
    </span>
  );
  const owners = assignees.filter((a) => a.role === 'OPS');
  const closed = row.status === 'COMPLETED' || row.status === 'CANCELLED';
  if (!isManager || closed || owners.length === 0) return <div className="text-sm">{label}</div>;
  const items: UzDropdownMenuItem[] = [
    { type: 'label', label: 'Operations owner (all desks)' },
    ...owners.map<UzDropdownMenuItem>((a) => ({
      label: (
        <span className="flex w-full items-center gap-2">
          <span
            className={cn(
              'h-2 w-2 shrink-0 rounded-full',
              a.online ? 'bg-emerald-500' : 'bg-zinc-300',
            )}
          />
          <span className="flex-1 truncate">{a.fullName}</span>
        </span>
      ),
      disabled: a.id === row.coordinator?.id,
      onSelect: () => setOwner.mutate({ id: row.id, userId: a.id }),
    })),
  ];
  return (
    <div onClick={(e) => e.stopPropagation()} className="text-sm">
      <UzDropdownMenu
        align="start"
        contentClassName="max-h-72 overflow-y-auto"
        trigger={
          <button
            type="button"
            className="flex max-w-[240px] items-center gap-1 rounded-md px-1.5 py-1 hover:bg-zinc-100"
          >
            {label}
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
          </button>
        }
        items={items}
      />
    </div>
  );
};

/** Every desk of the lead with its own owner; the desk chip opens that desk's bookings. */
const AssigneeCell = ({
  row,
  isManager,
  assignees,
  onOpenDesk,
}: {
  row: OpsCaseRow;
  isManager: boolean;
  assignees: OpsAssignee[];
  onOpenDesk: (desk: OpsDesk) => void;
}): React.JSX.Element => {
  const desks: OpsDesk[] = row.desks.length ? row.desks : [row];
  return (
    <div className="flex min-w-[220px] flex-col gap-1">
      <CoordinatorLine row={row} isManager={isManager} assignees={assignees} />
      {desks.map((d) => (
        <div key={d.id} className="flex items-center gap-2">
          <button
            type="button"
            className="shrink-0"
            title={`Open ${DEPARTMENT_META[d.department].label} bookings`}
            onClick={(e) => {
              e.stopPropagation();
              onOpenDesk(d);
            }}
          >
            <DepartmentChip department={d.department} className="w-[88px] justify-center" />
          </button>
          <DeskAssignee desk={d} isManager={isManager} assignees={assignees} />
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Grid (one per tab)
// ---------------------------------------------------------------------------

const ConvertedLeadsGrid = ({
  view,
  assignedToId,
  isManager,
  isAdmin,
  meId,
}: {
  view: OpsCaseView;
  assignedToId: string | null;
  isManager: boolean;
  isAdmin: boolean;
  meId: string | undefined;
}): React.JSX.Element => {
  const open = useOpenOpsCase();
  const complete = useCompleteOpsCase();
  const alreadyBooked = useMarkAlreadyBooked();
  const reopen = useReopenOpsCase();
  const { data: assignees = [] } = useOpsAssignees(isManager);

  const canWork = (row: OpsCaseRow): boolean =>
    isManager || row.assignedTo?.id === meId || row.coordinator?.id === meId;

  /**
   * The work surface: hold / issue every item of one desk. Opens in a new tab
   * so the queue stays put. A row stands for the whole lead, so it opens the
   * desk I own when I own one, else the first desk.
   */
  const openDesk = (desk: Pick<OpsDesk, 'id' | 'status' | 'assignedTo'>): void => {
    if (desk.status === 'ASSIGNED' && desk.assignedTo?.id === meId) open.mutate(desk.id);
    window.open(`/operations/converted-leads/${desk.id}`, '_blank', 'noopener,noreferrer');
  };
  const openBookings = (row: OpsCaseRow): void =>
    openDesk(row.desks.find((d) => d.assignedTo?.id === meId) ?? row);

  const columns: DataGridColumn<OpsCaseRow>[] = [
    {
      id: 'lead',
      header: 'Converted lead',
      alwaysVisible: true,
      cell: (r) => (
        <div className="min-w-[180px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-zinc-900">{r.customerName}</span>
            {view === 'all' ? (
              <UzBadge tone={STATUS_META[r.status].tone}>{STATUS_META[r.status].label}</UzBadge>
            ) : null}
          </div>
          <div className="text-xs text-zinc-500">
            {r.leadReference}
            {r.customerMobile ? ` · ${r.customerMobile}` : ''}
          </div>
          {r.salesOwnerName ? (
            <div className="text-[11px] text-zinc-400">Sold by {r.salesOwnerName}</div>
          ) : null}
        </div>
      ),
    },
    {
      id: 'trip',
      header: 'Trip',
      cell: (r) => (
        <div className="min-w-[180px] text-sm">
          <div className="flex items-center gap-1.5 font-medium text-zinc-900">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
            <span className="truncate">{r.tripTitle ?? r.destinationName ?? '-'}</span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-zinc-500">
            {r.tripScope ? (
              <span className="inline-flex items-center gap-1">
                <Globe2 className="h-3 w-3" />
                {r.tripScope === 'INTERNATIONAL' ? 'International' : 'Domestic'}
              </span>
            ) : null}
            {r.travellerCount ? (
              <span className="inline-flex items-center gap-1">
                <Users className="h-3 w-3" />
                {r.travellerCount} pax
              </span>
            ) : null}
            <span className="font-medium text-zinc-700">{inr(r.saleAmount)}</span>
          </div>
        </div>
      ),
    },
    {
      id: 'destination',
      header: 'Destination',
      cell: (r) => (
        <div className="flex items-center gap-1.5 text-sm whitespace-nowrap text-zinc-900">
          <Globe2 className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
          {r.destinationName ?? <span className="text-zinc-400">-</span>}
        </div>
      ),
    },
    {
      id: 'travelStartDate',
      header: 'Travel',
      sortable: true,
      cell: (r) => (
        <div className="text-sm whitespace-nowrap">
          <div className="flex items-center gap-1.5 text-zinc-900">
            <CalendarDays className="h-3.5 w-3.5 text-zinc-400" />
            {fmtDate(r.travelStartDate)}
          </div>
          {daysToTravel(r.travelStartDate) ? (
            <div className="text-xs text-zinc-500">{daysToTravel(r.travelStartDate)}</div>
          ) : null}
        </div>
      ),
    },
    {
      id: 'actionable',
      header: 'To book',
      cell: (r) => <ActionableCell actionable={r.actionable} nextPaymentDue={r.nextPaymentDue} />,
    },
    {
      id: 'documents',
      header: 'Documents',
      cell: (r) =>
        r.documentStatus ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openBookings(r);
            }}
            className="inline-flex items-center gap-1"
            title="Open bookings (travellers as uploaded are on the overview)"
          >
            <FileCheck2 className="h-3.5 w-3.5 text-zinc-400" />
            <UzBadge tone={DOC_META[r.documentStatus].tone}>
              {DOC_META[r.documentStatus].label}
            </UzBadge>
          </button>
        ) : (
          <span className="text-xs text-zinc-400">-</span>
        ),
    },
    {
      id: 'assignee',
      header: 'Ops owner',
      cell: (r) => (
        <AssigneeCell row={r} isManager={isManager} assignees={assignees} onOpenDesk={openDesk} />
      ),
    },
    {
      id: 'queuedAt',
      header: 'Converted',
      sortable: true,
      cell: (r) => (
        <div className="text-xs whitespace-nowrap text-zinc-500">
          <div className="text-zinc-700">{ago(r.queuedAt)}</div>
          {r.assignedAt ? <div>assigned {ago(r.assignedAt)}</div> : null}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      alwaysVisible: true,
      align: 'right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          {(r.status === 'ASSIGNED' || r.status === 'IN_PROGRESS') && canWork(r) ? (
            <UzButton size="sm" onClick={() => openBookings(r)}>
              {r.status === 'ASSIGNED' ? 'Start' : 'Bookings'}
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </UzButton>
          ) : (
            <UzButton size="sm" variant="outline" onClick={() => openBookings(r)}>
              Bookings
            </UzButton>
          )}
          {/* Admin short cut: the lead was booked outside the system, so every
              open desk closes at once without issuing items. */}
          {isAdmin && OPEN_STATUSES.has(r.status) ? (
            <UzButton
              size="sm"
              variant="outline"
              title="Booked outside the system - close every desk of this lead"
              loading={alreadyBooked.isPending && alreadyBooked.variables === r.id}
              onClick={() => {
                if (
                  window.confirm(
                    `Mark ${r.customerName}'s lead as already booked? Every desk closes.`,
                  )
                )
                  alreadyBooked.mutate(r.id);
              }}
            >
              <CheckCheck className="mr-1 h-3.5 w-3.5" /> Already booked
            </UzButton>
          ) : null}
          {/* "Done" only once bookings are under way. Removing a lead from the ops
              queue is not offered here any more. */}
          {r.status === 'IN_PROGRESS' && canWork(r) ? (
            <UzButton
              size="sm"
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              title="Every item is issued - close this lead for operations"
              loading={complete.isPending && complete.variables === r.id}
              onClick={() => complete.mutate(r.id)}
            >
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Done
            </UzButton>
          ) : null}
          {r.status === 'COMPLETED' && canWork(r) ? (
            <UzButton size="sm" variant="ghost" title="Reopen" onClick={() => reopen.mutate(r.id)}>
              <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reopen
            </UzButton>
          ) : null}
        </div>
      ),
    },
  ];

  const gridDefaultSort =
    view === 'completed' || view === 'all'
      ? { by: 'queuedAt', dir: 'desc' as const }
      : { by: 'queuedAt', dir: 'asc' as const };

  return (
    <>
      <ListScreen<OpsCaseRow, OpsCaseListQuery>
        embedded
        gridId={`converted-leads-${view}`}
        tableLayout="auto"
        title="Converted leads"
        useList={useOpsCasesList}
        defaultSort={gridDefaultSort}
        defaultPageSize={25}
        toQuery={(_state, base) => ({
          view,
          page: base.page,
          pageSize: base.pageSize,
          sortBy: base.sortBy,
          sortDir: base.sortDir,
          ...(base.search ? { search: base.search } : {}),
          ...(assignedToId ? { assignedToId } : {}),
        })}
        columns={columns}
        getRowId={(r) => r.id}
        onRowClick={(r) => openBookings(r)}
        copy={{
          searchPlaceholder: 'Search by customer, mobile or lead reference',
          emptyTitle:
            view === 'fresh'
              ? 'No fresh converted leads'
              : view === 'unassigned'
                ? 'Nothing waiting in the queue'
                : 'Nothing here yet',
          emptyDescription:
            view === 'fresh'
              ? 'A lead shows up here as soon as the customer has signed the contract and uploaded their documents.'
              : view === 'unassigned'
                ? 'Queued leads go out round-robin to ops executives who are logged in, within five minutes.'
                : undefined,
        }}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const ConvertedLeadsPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const isManager = me?.role === 'ADMIN' || me?.role === 'OPERATIONS_LEAD';
  // The Operations owner also gets the queue: their own leads' undealt desks.
  const seesQueue = isManager || me?.role === 'OPS';

  const [params, setParams] = useSearchParams();
  const rawView = params.get('view');
  const requested: OpsCaseView = isView(rawView) ? rawView : 'fresh';
  const view: OpsCaseView = requested === 'unassigned' && !seesQueue ? 'fresh' : requested;
  const assignedToId = isManager ? params.get('assignee') : null;

  const setView = (next: OpsCaseView): void => {
    const p = new URLSearchParams(params);
    p.set('view', next);
    p.delete('page');
    setParams(p, { replace: true });
  };
  const setAssignee = (next: string | null): void => {
    const p = new URLSearchParams(params);
    if (next) p.set('assignee', next);
    else p.delete('assignee');
    setParams(p, { replace: true });
  };

  const summary = useOpsCaseSummary(assignedToId ?? undefined);
  // Approvals moved to their own Admin page (Operations approvals, 2026-10-26).
  const tab: string = view;
  const { data: assignees = [] } = useOpsAssignees(isManager);
  const [refreshing, setRefreshing] = useState(false);

  const grid = (v: OpsCaseView): React.JSX.Element => (
    <ConvertedLeadsGrid
      view={v}
      assignedToId={assignedToId}
      isManager={isManager}
      isAdmin={me?.role === 'ADMIN'}
      meId={me?.id}
    />
  );

  return (
    <PageShell
      title="Converted leads"
      description="Contract signed. Book every flight, hotel, activity and transfer listed against each lead; the Documents column shows what the customer still owes."
      actions={
        <div className="flex flex-wrap items-center gap-2 max-sm:w-[calc(100vw-2rem)]">
          {isManager ? (
            <div className="w-52">
              <UzSelect<string>
                name="opsAssigneeFilter"
                placeholder="All ops owners"
                value={assignedToId ?? ''}
                onValueChange={(v) => setAssignee(v || null)}
                contentClassName="max-h-60"
                descriptionRight
                options={[
                  { value: '', label: 'All ops owners' },
                  ...assignees.map((a) => ({
                    value: a.id,
                    label: a.fullName,
                    description: `${a.online ? 'online' : 'away'} · ${a.openCases} open`,
                  })),
                ]}
              />
            </div>
          ) : null}
          <UzButton
            variant="outline"
            size="sm"
            title="Refresh"
            disabled={refreshing}
            onClick={() => {
              setRefreshing(true);
              void summary.refetch().finally(() => setRefreshing(false));
            }}
          >
            <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
          </UzButton>
        </div>
      }
    >
      <UzTabs
        value={tab}
        onValueChange={(v) => {
          if (v === 'approvals') {
            const p = new URLSearchParams(params);
            p.set('view', 'approvals');
            setParams(p, { replace: true });
          } else setView(v as OpsCaseView);
        }}
        tabs={[
          {
            value: 'fresh',
            label: 'Fresh converted leads',
            count: summary.data?.fresh,
            content: grid('fresh'),
          },
          ...(seesQueue
            ? [
                {
                  value: 'unassigned',
                  label: 'Unassigned queue',
                  count: summary.data?.unassigned,
                  content: grid('unassigned'),
                },
              ]
            : []),
          {
            value: 'in_progress',
            label: 'In progress',
            count: summary.data?.inProgress,
            content: grid('in_progress'),
          },
          { value: 'completed', label: 'Completed', content: grid('completed') },
          { value: 'all', label: 'All', content: grid('all') },
        ]}
      />
    </PageShell>
  );
};
